/**
 * Module dependencies.
 */

import { ErrorField } from '../../error-field/error-field';
import { FormField } from '../../form-field/form-field';
import { Input } from 'app/components/ui/input';
import { cn } from 'app/lib/utils';
import { omit } from 'lodash';
import React, { forwardRef, useEffect, useState } from 'react';

/**
 * Represent a numeric form value as the text shown in the input (empty for
 * nullish/NaN).
 */

function toText(value?: number | string | null): string {
  if (value === undefined || value === null || (typeof value === 'number' && Number.isNaN(value))) {
    return '';
  }

  return String(value);
}

/**
 * Keep only a valid decimal shape: an optional leading minus, digits and a
 * single dot. Letters and stray symbols are dropped so the field can never end
 * up holding `NaN`.
 */

function sanitize(raw: string): string {
  return raw
    .replace(/[^\d.-]/g, '')
    .replace(/(?!^)-/g, '')
    .replace(/(\..*)\./g, '$1');
}

/**
 * `InputNumberProps` type.
 */

export type InputNumberProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> & {
  className?: string;
  error: any;
  formatter?: (formValues: any) => any;
  invalid?: boolean;
  isDirty?: boolean;
  isTouched?: boolean;
  label: string;
  onChange?: (value: number | undefined) => void;
  parser?: (formValues: any) => any;
  value?: number | string | null;
};

/**
 * Export `InputNumberComponent` component.
 */

export function InputNumberComponent(props: InputNumberProps, forwardedRef: any): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { className, invalid, isTouched, isDirty, label, onChange, value, ...rest } = props;
  const { error } = rest;

  // Keep the raw text locally so partial entries like "1." or "-" survive while
  // typing; only a parsed number (or undefined) is pushed up to the form.
  const [text, setText] = useState<string>(() => toText(value));

  // Re-sync from the outside (reset, edit prefill) — but never fight the user
  // mid-typing when the current text already represents the same number.
  useEffect(() => {
    const current = parseFloat(text);
    const normalized = Number.isNaN(current) ? undefined : current;

    if (value !== normalized) {
      setText(toText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <FormField className={className} label={label}>
      <Input
        className={cn({ 'cursor-not-allowed opacity-50': rest.disabled })}
        inputMode={'decimal'}
        onChange={event => {
          const cleaned = sanitize(event.target.value);

          setText(cleaned);

          if (onChange) {
            const parsed = parseFloat(cleaned);

            onChange(Number.isNaN(parsed) ? undefined : parsed);
          }
        }}
        ref={forwardedRef}
        value={text}
        {...omit(rest, ['error', 'formatter', 'htmlType', 'parser'])}
      />

      <ErrorField error={error} isVisible={invalid || isTouched} />
    </FormField>
  );
}

/**
 * Export `InputNumber` component.
 */

export const InputNumber = forwardRef<any, InputNumberProps>(InputNumberComponent);
